import React, { useState, MouseEvent } from 'react';
import TablePagination from '@mui/material/TablePagination';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import ScorecardManagementTableRowViewModel from './Viewmodels/ScorecardManagementTableRowViewModel';
import ScorecardManagementTableRow from './ScorecardManagementTableRow';
import SortableTableHead, { HeadCell } from '../Utilities/SortableTableHead';
import TableContainer from '@mui/material/TableContainer';
import ResponsiveTable from '../Utilities/ResponsiveTable';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import AddEditScorecardDetailsModal from './AddEditScorecardDetailsModal';
import ScorecardFiltersViewModel from './Viewmodels/ScorecardFiltersViewModel';

type Props = {
    rows: ScorecardManagementTableRowViewModel[];
    count: number;
    filters: ScorecardFiltersViewModel;
    changeFilter: (filterName: keyof ScorecardFiltersViewModel, newValue: any) => void;
    refresh: boolean;
    setScorecardId: (id: number) => void;
    loading: boolean;
    getData: () => void;
}

const styles = {
    tableCell: {
        padding: '4px 12px 4px 12px',
    },
    table: {
        minWidth: 650,
    }
}

const headCells: HeadCell<ScorecardManagementTableRowViewModel>[] = [
    { id: 'name', property: 'Name', numeric: false, disablePadding: false, label: 'Name' },
    { id: 'client', property: 'Client', numeric: false, disablePadding: false, label: 'Client' },
    { id: 'channel', property: 'Channel', numeric: false, disablePadding: false, label: 'Channel' },
    { id: 'scorecardType', property: 'ScorecardType', numeric: false, disablePadding: false, label: 'Type' },
    { id: 'customerLevel', property: 'CustomerLevel', numeric: false, disablePadding: false, label: 'Customer Level' },
    { id: 'passThreshold', property: 'PassThreshold', numeric: true, disablePadding: false, label: 'Pass Threshold' },
    { id: 'resQPassThreshold', property: 'ResQPassThreshold', numeric: true, disablePadding: false, label: 'ResQ Pass Threshold' },
    { id: 'active', property: 'Active', numeric: false, disablePadding: false, label: 'Active' },
]

export default function ScorecardManagementTable({ rows, count, filters, changeFilter, refresh, setScorecardId, loading, getData }: Props) {
    const [editId, setEditId] = useState(0);
    const [openEditScorecard, setOpenEditScorecard] = useState(false);

    const handleChangePage = (event: unknown, newPage: number) => {
        changeFilter('page', newPage);
    }

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        changeFilter('rowsPerPage', parseInt(event.target.value, 10));
        changeFilter('page', 0);
    }

    const handleRequestSort = (event: MouseEvent<unknown>, property: keyof ScorecardManagementTableRowViewModel) => {
        const isAsc = filters.orderBy === property && filters.order === 'asc';
        changeFilter('order', isAsc ? 'desc' : 'asc');
        changeFilter('orderBy', property);
    }

    const edit = (id: number) => {
        setEditId(id);
        setOpenEditScorecard(true);
    }

    const closeEditScorecard = (refresh: boolean, templateId?: number) => {
        if (refresh) getData();
        setOpenEditScorecard(false);
        if (templateId) setScorecardId(templateId);
    }

    return (
        <TableContainer>
            <ResponsiveTable
                sx={styles.table}
                aria-labelledby="tableTitle"
                size="medium"
                aria-label="Site Log Table"
            >
                <SortableTableHead
                    order={filters.order}
                    orderBy={filters.orderBy}
                    onRequestSort={handleRequestSort}
                    headCells={headCells}
                    lastCells={<TableCell align="right">Actions</TableCell>}
                />
                <TableBody>
                    {loading &&
                        <TableRow style={{ height: 53 }}>
                            <TableCell colSpan={11} align="center"><CircularProgress color="secondary" /><Typography>Pulling latest data</Typography></TableCell>
                        </TableRow>
                    }
                    {!loading && rows.length <= 0 &&
                        <TableRow>
                            <TableCell align="center" colSpan={12} sx={styles.tableCell}>No records found</TableCell>
                        </TableRow>
                    }
                    {!loading && rows.length > 0 && rows.map((row, index) =>
                        <ScorecardManagementTableRow key={index} row={row} getData={getData} edit={edit} />
                    )}
                </TableBody>
            </ResponsiveTable>
            <TablePagination
                rowsPerPageOptions={[5, 10, 25]}
                component="div"
                count={count}
                rowsPerPage={filters.rowsPerPage}
                page={filters.page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
            />
            <AddEditScorecardDetailsModal open={openEditScorecard} onClose={closeEditScorecard} editId={editId} />
        </TableContainer>
    );
}